import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Smartphone, TrendingUp, Shield, Zap, ExternalLink } from 'lucide-react';
import { groupInfo, leadershipTeam, newsEvents, innovation } from '../mock';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const Home = () => {
  // Get only 6 news items for homepage
  const featuredNews = newsEvents
  .filter(item => item.featured === true)
  .sort((a, b) => new Date(b.date) - new Date(a.date))
  .slice(0, 6);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-purple-50 via-white to-purple-50 py-8 md:py-20 overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-in fade-in slide-in-from-left duration-700">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-purple-900 leading-tight">
                {groupInfo.name}
              </h1>
              <p className="text-2xl text-purple-700 font-medium">
                {groupInfo.tagline}
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                {groupInfo.description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/about">
                  <Button className="border-2 border-purple-700 bg-purple-600 hover:bg-purple-700 text-white px-4 md:px-8 py-6 text-lg rounded-full transition-all duration-200 shadow-lg hover:shadow-xl">
                    Learn More <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="border-2 border-purple-600 text-purple-600 hover:bg-purple-50 px-4 md:px-8 py-6 text-lg rounded-full transition-all duration-200">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative animate-in fade-in slide-in-from-right duration-700">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src={groupInfo.bannerImage}
                  alt="Jagratha SHG Group Members"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-purple-900/30 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Leadership Team Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl  font-bold text-purple-900 mb-4">Our Leadership Team</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Meet the dedicated leaders guiding our journey towards empowerment and growth
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            {leadershipTeam.map((member, index) => (
              <Card 
                key={member.id} 
                className="group hover:shadow-xl transition-all duration-300 border-purple-100 hover:border-purple-300 animate-in fade-in zoom-in duration-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <CardContent className="p-6 text-center">
                  <div className="relative mb-4 mx-auto w-32 h-32">
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover ring-4 ring-purple-100 group-hover:ring-purple-300 transition-all duration-300"
                    />
                    <div className="absolute inset-0 rounded-full bg-purple-600 opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
                  </div>
                  <h3 className="text-xl font-bold text-purple-900 mb-1">{member.name}</h3>
                  <p className="text-purple-600 font-semibold mb-3">{member.position}</p>
                  <p className="text-gray-600 text-sm leading-relaxed">{member.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* News & Events Section */}
      <section className="py-20 bg-gradient-to-b from-purple-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-purple-900 mb-4">News & Events</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Stay updated with our latest activities, achievements, and upcoming events
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredNews.map((news, index) => (
              <Card 
                key={news.id} 
                className="group hover:shadow-xl transition-all duration-300 overflow-hidden border-purple-100 hover:border-purple-300 animate-in fade-in slide-in-from-bottom duration-500"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative h-48 overflow-hidden">
                  <Link to={`/news/${news.id}`}><img
                    src={news.thumbnail}
                    alt={news.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  /></Link>
                  <div className="absolute top-4 right-4">
                    <span className="bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {news.category}
                    </span>
                  </div>
                </div>
                <CardContent className="p-6">
                  <p className="text-sm text-purple-600 font-medium mb-2">
                    {new Date(news.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                  </p>
                  <h3 className="text-xl font-bold text-purple-900 mb-3 line-clamp-2"><Link to={`/news/${news.id}`}>{news.title}</Link></h3>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3 leading-relaxed">{news.shortDescription}</p>
                  <Link to={`/news/${news.id}`}>
                    <Button variant="link" className="text-purple-600 hover:text-purple-700 p-0 h-auto font-semibold group/btn">
                      Read More 
                      <ArrowRight className="ml-2 group-hover/btn:translate-x-1 transition-transform duration-200" size={16} />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/news-events">
              <Button className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-200 shadow-lg hover:shadow-xl">
                View All News & Events <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Innovation Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-purple-600 to-purple-800 rounded-3xl overflow-hidden shadow-2xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
              <div className="p-6 md:p-12 lg:p-16 text-white space-y-6">
                <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold">
                  Future Ready
                </div>
                <h2 className="text-4xl font-bold">{innovation.title}</h2>
                <p className="text-purple-100 text-lg leading-relaxed">
                  {innovation.description}
                </p>
                <div className="space-y-4 pt-4">
                  {innovation.features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-3">
                      <div className="bg-white/20 backdrop-blur-sm rounded-full p-2 mt-1">
                        {index === 0 && <Smartphone size={20} />}
                        {index === 1 && <Zap size={20} />}
                        {index === 2 && <Shield size={20} />}
                        {index === 3 && <TrendingUp size={20} />}
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{feature.title}</h3>
                        <p className="text-purple-100">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
                <a
                  href={innovation.appLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center space-x-2 bg-white text-purple-700 px-8 py-4 rounded-full hover:bg-purple-50 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl mt-6"
                >
                  <span>Download <span className='hidden md:inline-block'>Mobile</span> App</span>
                  <ExternalLink size={20} />
                </a>
              </div>
              <div className="relative h-full min-h-[400px] lg:min-h-0">
                <img
                  src="images/jagratha-app-photo-1.jpg"
                  alt="Digital Innovation"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/50 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
